import { degrees, PDFDocument, rgb, StandardFonts } from "pdf-lib";

// 🔁 Fonction réutilisable

export const loadImage = async (file: File | string): Promise<HTMLImageElement> => {
  const image = new Image();
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    if (typeof file === "string") {
      image.src = file;
    } else {
      const reader = new FileReader();

      reader.onload = (event) => {
        image.src = event.target?.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    }
  });
  return image;
};


export async function renderWatermarkCanvas(
  text: string,
  canvas: HTMLCanvasElement,
  imgSrc: File | string,
  watermarkSrc: File | string
): Promise<HTMLCanvasElement | undefined> {
  try {
    const [image, watermark] = await Promise.all([
      loadImage(imgSrc),
      loadImage(watermarkSrc),
    ]);

    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    canvas.width = image.width;
    canvas.height = image.height;

    // === 1. Draw original image ===
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    // === 2. Draw diagonal repeating text ===
    const now = new Date();
    const formattedDate = now.toISOString().split("T").join(" ").split(".")[0];
    const watermarkText = `• ${text} - ${formattedDate}`;

    const fontSize = Math.round(Math.min(canvas.width, canvas.height) / 42);
    context.font = `${fontSize}px DejaVu Sans, Arial, sans-serif`;
    context.fillStyle = "rgba(25, 86, 168, 1)";
    context.textAlign = "left";
    context.textBaseline = "top";

    const textWidth = context.measureText(watermarkText).width;

    // 🔁 Proportional spacing (based on A4 dimensions)
    const baseWidth = 1240;
    const baseHeight = 1704;
    const spacingX = textWidth + (125 * canvas.width) / baseWidth;
    const spacingY = fontSize + (350 * canvas.height) / baseHeight;

    for (let y = -canvas.height; y < canvas.height * 2; y += spacingY) {
      for (let x = -canvas.width; x < canvas.width * 2; x += spacingX) {
        context.save();
        context.translate(x, y);
        context.rotate((-30 * Math.PI) / 180);
        context.fillText(watermarkText, 0, 0);
        context.restore();
      }
    }

    // === 3. Draw center watermark image proportionally ===
    const scaleFactor = 0.4;
    const maxWatermarkWidth = canvas.width * scaleFactor;
    const maxWatermarkHeight = canvas.height * scaleFactor;

    const aspectRatio = watermark.width / watermark.height;

    let displayWidth = maxWatermarkWidth;
    let displayHeight = maxWatermarkWidth / aspectRatio;

    if (displayHeight > maxWatermarkHeight) {
      displayHeight = maxWatermarkHeight;
      displayWidth = maxWatermarkHeight * aspectRatio;
    }

    const centerX = (canvas.width - displayWidth) / 2;
    const centerY = (canvas.height - displayHeight) / 2;

    context.drawImage(watermark, centerX, centerY, displayWidth, displayHeight);

    return canvas;
  } catch (error) {
    console.error("Error rendering watermark canvas:", error);
  }
}

// 🔁 Fonction réutilisable : retourne les pdfBytes
export async function renderWatermarkPDF(
  pdfBuffer: ArrayBuffer,
  watermarkImageSrc: string,
  text: string
): Promise<Uint8Array | undefined> {
  let pdfDoc: PDFDocument;

  try {
    pdfDoc = await PDFDocument.load(pdfBuffer);
  } catch (error) {
    console.error("Error loading PDF:", error);
    throw new Error("PDF non lisible ou encrypté");
  }

  const watermarkImageBytes = await fetch(watermarkImageSrc).then((res) =>
    res.arrayBuffer()
  );
  const watermarkImage = await pdfDoc.embedPng(watermarkImageBytes);
  const watermarkFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const now = new Date();
  const formattedDate = now.toISOString().split("T").join(" ").split(".")[0];
  const watermarkText = `• ${text} - ${formattedDate}`;

  const baseWidth = 1204;
  const baseHeight = 1704;

  const pages = pdfDoc.getPages();

  for (const page of pages) {
    const { width, height } = page.getSize();

    // 1. Draw repeated diagonal watermark text
    const fontSize = Math.round(Math.min(width, height) / 40);
    const textWidth = watermarkFont.widthOfTextAtSize(watermarkText, fontSize);
    const textHeight = watermarkFont.heightAtSize(fontSize);

    const spacingX = textWidth + (155 * width) / baseWidth;
    const spacingY = textHeight + (250 * height) / baseHeight;

    for (let y = -height; y < height * 2; y += spacingY) {
      for (let x = -width; x < width * 2; x += spacingX) {
        page.drawText(watermarkText, {
          x,
          y,
          size: fontSize,
          font: watermarkFont,
          color: rgb(25 / 255, 86 / 255, 168 / 255),
          rotate: degrees(30),
          opacity: 1,
        });
      }
    }

    // 2. Draw watermark image (proportional & centered)
    const scaleFactor = 0.3;
    const maxWatermarkWidth = width * scaleFactor;
    const maxWatermarkHeight = height * scaleFactor;

    const originalDims = watermarkImage.scale(1);
    const imgAspectRatio = originalDims.width / originalDims.height;

    let displayWidth = maxWatermarkWidth;
    let displayHeight = maxWatermarkWidth / imgAspectRatio;

    if (displayHeight > maxWatermarkHeight) {
      displayHeight = maxWatermarkHeight;
      displayWidth = maxWatermarkHeight * imgAspectRatio;
    }

    const centerX = (width - displayWidth) / 2;
    const centerY = (height - displayHeight) / 2;

    page.drawImage(watermarkImage, {
      x: centerX,
      y: centerY,
      width: displayWidth,
      height: displayHeight,
      opacity: 1,
    });
  }

  return await pdfDoc.save();
}
